import { Reducer, AnyAction } from 'redux';
import { ImportSetAdminListState } from './types-store';
import {
  ACTION, ClearList,
  RequestList, ReceiveList, FailList,
  RequestDeleteImportSet, ReceiveDeleteImportSet, FailDeleteImportSet, 
  ClearAddImportSet, AddImportSetFieldsByKey, UpdateAddImportSetIsDirty, 
  UpdateAddImportSetState, ResetAddImportSetValidation, UpdateAddImportSetValidation
} from './action-types';

export const INITIAL_STATE: ImportSetAdminListState = {
  requestId: 0,
  fetching: false,
  requestedSearchModel: undefined,
  numberOfResults: undefined,
  listItems: undefined,
  deletingImportSets: [],
  addImportSet: {
    state: 0
  },
  fetchingAddImportSet: undefined
};

const reducers: { [indexer: string]: Reducer<ImportSetAdminListState> } = {

  [ACTION.CLEAR_LIST]: (state: ImportSetAdminListState, action: ClearList): ImportSetAdminListState => {
    return {
      ...state,
      requestId: action.requestId,
      fetching: false,
      requestedSearchModel: undefined,
      numberOfResults: undefined,
      listItems: undefined,
      deletingImportSets: []
    };
  },

  [ACTION.REQUEST_LIST]: (state: ImportSetAdminListState, action: RequestList): ImportSetAdminListState => {

    return {
      ...state,
      requestId: action.requestId,
      fetching: true,
      requestedSearchModel: action.model,
      ...(action.clearResults && {
        numberOfResults: undefined,
        listItems: undefined,
        deletingImportSets: []
      })
    };
  },

  [ACTION.RECEIVE_LIST]: (state: ImportSetAdminListState, action: ReceiveList): ImportSetAdminListState => {

    if (action.requestId !== state.requestId) {
      return state;
    }

    return {
      ...state,
      fetching: false,
      numberOfResults: action.numberOfResults,
      listItems: [
        ...(state.listItems || []),
        ...action.list
      ]
    };
  },

  [ACTION.FAIL_LIST]: (state: ImportSetAdminListState, action: FailList): ImportSetAdminListState => {

    if (action.requestId !== state.requestId) {
      return state;
    }

    return {
      ...state,
      fetching: false
    };
  },

  [ACTION.REQUEST_DELETE_IMPORT_SET]: (state: ImportSetAdminListState,
    action: RequestDeleteImportSet): ImportSetAdminListState => {

    return !state.deletingImportSets || !state.deletingImportSets.includes(action.id)
      ? {
        ...state,
        deletingImportSets: [
          ...(state.deletingImportSets || []),
          action.id
        ]
      }
      : state;
  },

  [ACTION.RECEIVE_DELETE_IMPORT_SET]: (state: ImportSetAdminListState,
    action: ReceiveDeleteImportSet): ImportSetAdminListState => {

    if (!state.deletingImportSets || !state.deletingImportSets.includes(action.id) || !state.listItems?.length) {
      return state;
    }

    const listItems = (state.listItems || []).filter(item => item.value !== action.id);

    return {
      ...state,
      listItems,
      numberOfResults: state.numberOfResults - (state.listItems.length - listItems.length),
      deletingImportSets: state.deletingImportSets.filter(id => id !== action.id)
    };
  },

  [ACTION.FAIL_DELETE_IMPORT_SET]: (state: ImportSetAdminListState,
    action: FailDeleteImportSet): ImportSetAdminListState => {

    return state.deletingImportSets && state.deletingImportSets.includes(action.id)
      ? {
        ...state,
        deletingImportSets: state.deletingImportSets.filter(id => id !== action.id)
      }
      : state;
  },

  [ACTION.CLEAR_ADD_IMPORT_SET]: (state: ImportSetAdminListState, action: ClearAddImportSet): ImportSetAdminListState => {

    return {
      ...state,
      addImportSet: {
        state: 0
      }
    };
  },

  [ACTION.CHANGE_ADD_IMPORT_SET_FIELDS_BY_KEY]: (state: ImportSetAdminListState,
    action: AddImportSetFieldsByKey): ImportSetAdminListState => {
    return {
      ...state,
      addImportSet: {
        ...state.addImportSet,
        form: {
          ...state.addImportSet?.form ?? {},
          [action.key]: action.value
        }
      }
    };
  },

  [ACTION.UPDATE_ADD_IMPORT_SET_IS_DIRTY]: (state: ImportSetAdminListState,
    action: UpdateAddImportSetIsDirty): ImportSetAdminListState => {

    return {
      ...state,
      addImportSet: {
        ...state.addImportSet,
        isDirty: action.isDirty
      }
    };
  },

  [ACTION.UPDATE_ADD_IMPORT_SET_STATE]: (state: ImportSetAdminListState,
    action: UpdateAddImportSetState): ImportSetAdminListState => {

    return {
      ...state,
      addImportSet: {
        ...state.addImportSet,
        state: action.state
      }
    };
  },

  [ACTION.RESET_ADD_IMPORT_SET_VALIDATION]: (state: ImportSetAdminListState,
    action: ResetAddImportSetValidation): ImportSetAdminListState => {
    const validation = state?.addImportSet?.validation;
    const { [action.key]: undefined, ...updatedValidation } = validation ?? {};

    return state?.addImportSet?.validation ? {
      ...state,
      addImportSet: {
        ...state.addImportSet,
        validation: updatedValidation
      }
    } : state;
  },

  [ACTION.UPDATE_ADD_IMPORT_SET_VALIDATION]: (state: ImportSetAdminListState,
    action: UpdateAddImportSetValidation): ImportSetAdminListState => {

    return {
      ...state,
      addImportSet: {
        ...state.addImportSet,
        validation: action.validation
      }
    };
  }
};

export const reducer: Reducer<ImportSetAdminListState> = (
  state: ImportSetAdminListState = INITIAL_STATE, action: AnyAction) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};
